import {useLayoutEffect, useRef, useState} from "react";

const TAG_CHIP_SELECTOR = '[data-product-tag-chip]';
const OVERFLOW_SELECTOR = '[data-product-tag-overflow]';

const resetChipVisibility = (chips) => {
    chips.forEach((chip) => {
        chip.style.display = '';
    });
};

const getMaxContainerHeight = (container, maxLines) => {
    const chips = container.querySelectorAll(TAG_CHIP_SELECTOR);
    if (!chips.length) {
        return null;
    }

    const style = getComputedStyle(container);
    const gap = parseFloat(style.rowGap || style.gap) || 0;
    const chipHeight = chips[0].getBoundingClientRect().height;

    if (!chipHeight) {
        return null;
    }

    return chipHeight * maxLines + gap * Math.max(0, maxLines - 1) + 1;
};

/**
 * Synchronously finds how many tags fit in maxLines, including the +N badge when needed.
 * Mutates visibility on the measure container only; resets before returning.
 */
export const measureTagVisibleCount = (measureContainer, tagCount, maxLines) => {
    if (!measureContainer || !maxLines || tagCount <= 0) {
        return tagCount;
    }

    const chips = [...measureContainer.querySelectorAll(TAG_CHIP_SELECTOR)];
    const overflowEl = measureContainer.querySelector(OVERFLOW_SELECTOR);

    if (!chips.length) {
        return tagCount;
    }

    const maxHeight = getMaxContainerHeight(measureContainer, maxLines);
    if (maxHeight === null) {
        return tagCount;
    }

    const applyLayout = (visibleCount) => {
        chips.forEach((chip, index) => {
            chip.style.display = index < visibleCount ? '' : 'none';
        });

        if (overflowEl) {
            const overflowCount = tagCount - visibleCount;
            overflowEl.style.display = overflowCount > 0 ? '' : 'none';
            overflowEl.textContent = overflowCount > 0 ? `+${overflowCount}` : '';
        }
    };

    applyLayout(tagCount);
    if (measureContainer.scrollHeight <= maxHeight) {
        resetChipVisibility(chips);
        if (overflowEl) {
            overflowEl.style.display = 'none';
        }
        return tagCount;
    }

    for (let visibleCount = tagCount - 1; visibleCount >= 0; visibleCount -= 1) {
        applyLayout(visibleCount);

        if (measureContainer.scrollHeight <= maxHeight) {
            resetChipVisibility(chips);
            if (overflowEl) {
                overflowEl.style.display = 'none';
            }
            return visibleCount;
        }
    }

    resetChipVisibility(chips);
    if (overflowEl) {
        overflowEl.style.display = 'none';
    }
    return 0;
};

/**
 * Computes how many tags fit within the given number of flex-wrap rows using a hidden measure container.
 */
const useTagLineOverflow = ({
                                measureRef,
                                tagCount,
                                maxLines,
                                enabled = true,
                                remeasureKey = 0,
                            }) => {
    const [visibleCount, setVisibleCount] = useState(tagCount);
    const lastWidthRef = useRef(null);

    useLayoutEffect(() => {
        if (!enabled || !maxLines || tagCount <= 0) {
            setVisibleCount(tagCount);
            return;
        }

        const measureContainer = measureRef.current;
        if (!measureContainer) {
            return;
        }

        const nextCount = measureTagVisibleCount(measureContainer, tagCount, maxLines);
        setVisibleCount(nextCount);
        lastWidthRef.current = measureContainer.clientWidth;
    }, [measureRef, tagCount, maxLines, enabled, remeasureKey]);

    useLayoutEffect(() => {
        if (!enabled || !maxLines || tagCount <= 0) {
            return;
        }

        const measureContainer = measureRef.current;
        if (!measureContainer) {
            return;
        }

        const observer = new ResizeObserver(() => {
            const width = measureContainer.clientWidth;
            if (lastWidthRef.current !== null && Math.abs(width - lastWidthRef.current) < 1) {
                return;
            }

            lastWidthRef.current = width;
            setVisibleCount(measureTagVisibleCount(measureContainer, tagCount, maxLines));
        });

        observer.observe(measureContainer);

        return () => observer.disconnect();
    }, [measureRef, tagCount, maxLines, enabled]);

    return visibleCount;
};

export default useTagLineOverflow;
